import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useAppDispatch } from '../../../../../hooks/redux';
import {
    getLiveGuidelineNotificationsAction,
    restoreLiveGuidelineProductAction,
} from '../../../../../redux/actions/liveGuidelineActions';
import CustomPagination from '../../../../../components/common/pagination/CustomPagination';
import {
    LiveGuidelineNotificationDayGroup,
    LiveGuidelineNotificationInformation,
} from '../../../../../redux/actions/liveGuidelineActions/types';
import { AppDispatch } from '../../../../../redux/store';
import { toShortUtcDate } from '../../../../../helpers/dateHelper';
import EmptyData from '../../../../../components/common/emptyData/EmptyData';
import Switcher from '../../../../../components/common/switcher/Switcher';
import { blueDark02 } from '../../../../../styles/colors';
import { getExternalProductsAction } from '../../../../../redux/actions/transparencyCatalogActions';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../../../redux/actions/transparencyCatalogActions/types';
import { flattenTcMfSectionProducts } from '../../../../../helpers/tcExternalProducts';
import ErrorModal from '../../../../../components/common/errorModal/ErrorModal';
import { useModalState } from '../../../../../hooks/modalState';
import {
    ActivitiesContainer,
    DayCount,
    DayLabel,
    NotificationSwitcherWrap,
    NotificationToggleButton,
    NotificationToggleRow,
    NotificationsFilterRow,
    NotificationsTabContainer,
} from './NotificationsTab.styles';
import NotificationActivityRow, { NotificationProduct } from './NotificationActivityRow';
import NotificationControls from './NotificationControls';
import { chunkArray } from '../../../../../helpers/array';

interface Props {
    liveGuidelineId: number;
    liveGuidelineName: string;
}

const PAGE_SIZE = 10;
const EXTERNAL_PRODUCTS_CHUNK_SIZE = 700;

interface NotificationDaysProps {
    days: LiveGuidelineNotificationDayGroup[];
    openDays: Set<string>;
    productsMap: Record<number, NotificationProduct>;
    liveGuidelineName: string;
    onToggleDay: (date: string) => void;
    onRestore: (productId: number) => void;
}

const NotificationDays: React.FC<NotificationDaysProps> = ({
    days,
    openDays,
    productsMap,
    liveGuidelineName,
    onToggleDay,
    onRestore,
}) => (
    <>
        {days.map((day) => {
            const isOpen = openDays.has(day.date);
            const count = day.authors.reduce((sum, author) => sum + author.activities.length, 0);

            return (
                <React.Fragment key={day.date}>
                    <NotificationToggleRow>
                        <NotificationToggleButton onClick={() => onToggleDay(day.date)}>
                            <NotificationSwitcherWrap>
                                <Switcher
                                    width={24}
                                    height={24}
                                    clicked={isOpen}
                                    stopPropagation={false}
                                    iconColor="white"
                                    iconColorActive="white"
                                    bgColorActive={blueDark02}
                                    bgColor={blueDark02}
                                />
                            </NotificationSwitcherWrap>
                            <DayLabel open={isOpen}>{toShortUtcDate(day.date)}</DayLabel>
                            <DayCount open={isOpen}>({count})</DayCount>
                        </NotificationToggleButton>
                    </NotificationToggleRow>
                    {isOpen && (
                        <ActivitiesContainer>
                            {day.authors.flatMap((author) =>
                                    author.activities.map((activity) => ({ author, activity })),
                                )
                                .sort(
                                    (a, b) =>
                                        new Date(b.activity.createdOn).getTime() -
                                        new Date(a.activity.createdOn).getTime(),
                                )
                                .map(({ author, activity }, index) => (
                                    <NotificationActivityRow
                                        key={activity.id ?? index}
                                        activity={activity}
                                        authorName={author.username}
                                        liveGuidelineName={liveGuidelineName}
                                        index={index}
                                        product={
                                            activity.productId ? productsMap[activity.productId] : undefined
                                        }
                                        onRestore={onRestore}
                                    />
                                ))}
                        </ActivitiesContainer>
                    )}
                </React.Fragment>
            );
        })}
    </>
);

const getExternalProductsMap = async (
    dispatch: AppDispatch,
    productIds: number[],
): Promise<Record<number, NotificationProduct> | null> => {
    if (!productIds.length) {
        return {};
    }

    // Fetch in batches so very large guidelines don't send an oversized request.
    const results = await Promise.all(
        chunkArray(productIds, EXTERNAL_PRODUCTS_CHUNK_SIZE).map((chunk) =>
            dispatch(getExternalProductsAction({ productIds: chunk })),
        ),
    );

    if (!results.every((result) => getExternalProductsAction.fulfilled.match(result))) {
        return null;
    }

    const products = results.flatMap((result) => {
        const response = result.payload as
            | { data?: GetProductsFromTransparencyCatalogAPIResponse }
            | undefined;
        return response?.data?.mfSection ? flattenTcMfSectionProducts(response.data.mfSection) : [];
    });

    const externalProductById = new Map(products.map((product) => [product.id, product]));

    return productIds.reduce<Record<number, NotificationProduct>>((map, productId) => {
        map[productId] = {
            productId,
            externalProduct: externalProductById.get(productId),
        };
        return map;
    }, {});
};

const getNotificationProductsMap = async (
    dispatch: AppDispatch,
    notifications: LiveGuidelineNotificationDayGroup[],
): Promise<Record<number, NotificationProduct> | null> => {
    const productIds = [
        ...new Set(
            notifications.flatMap((day) =>
                day.authors.flatMap((author) =>
                    author.activities
                        .map((activity) => activity.productId)
                        .filter((id): id is number => id != null),
                ),
            ),
        ),
    ];
    return getExternalProductsMap(dispatch, productIds);
};

interface NotificationPageData {
    notifications: LiveGuidelineNotificationDayGroup[];
    total: number;
    productsMap: Record<number, NotificationProduct>;
}

const loadNotificationPage = async (
    dispatch: AppDispatch,
    liveGuidelineId: number,
    page: number,
    showAction: string,
): Promise<NotificationPageData | null> => {
    const result = await dispatch(
        getLiveGuidelineNotificationsAction({
            liveGuidelineId,
            curPage: page,
            pageSize: PAGE_SIZE,
            showAction,
        }),
    );
    if (!getLiveGuidelineNotificationsAction.fulfilled.match(result)) {
        return null;
    }

    const response = result.payload as { data?: LiveGuidelineNotificationInformation } | undefined;
    const notifications = response?.data?.notifications ?? [];
    const productsMap = await getNotificationProductsMap(dispatch, notifications);
    if (productsMap === null) {
        return null;
    }

    return {
        notifications,
        total: response?.data?.totalPages ?? 1,
        productsMap,
    };
};

const toggleOpenNotificationDay = (
    setOpenDays: React.Dispatch<React.SetStateAction<Set<string>>>,
    date: string,
) => {
    setOpenDays((previous) => {
        const next = new Set(previous);
        if (next.has(date)) {
            next.delete(date);
        } else {
            next.add(date);
        }
        return next;
    });
};

const useNotificationData = (liveGuidelineId: number) => {
    const dispatch = useAppDispatch();
    const errorModal = useModalState();
    const [pending, setPending] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showAction, setShowAction] = useState('all');
    const [days, setDays] = useState<LiveGuidelineNotificationDayGroup[]>([]);
    const [openDays, setOpenDays] = useState<Set<string>>(new Set());
    const [productsMap, setProductsMap] = useState<Record<number, NotificationProduct>>({});

    const getNotifications = async (page = currentPage, action = showAction) => {
        setPending(true);
        try {
            const result = await loadNotificationPage(dispatch, liveGuidelineId, page, action);
            if (!result) {
                errorModal.onOpen();
                return;
            }
            setProductsMap(result.productsMap);
            setDays(result.notifications);
            setTotalPages(result.total);
            setCurrentPage(page);
            setOpenDays(new Set(result.notifications[0] ? [result.notifications[0].date] : []));
        } finally {
            setPending(false);
        }
    };

    useEffect(() => {
        getNotifications(1, showAction);
    }, [liveGuidelineId]);

    const changePageNumber = (page: number) => getNotifications(page, showAction);
    const handleShowActionChange = (value: string) => {
        setShowAction(value);
        getNotifications(1, value);
    };

    const handleCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const page = Number(event.currentTarget.value);
        if (Number.isInteger(page) && page > 0 && page <= Math.ceil(totalPages / PAGE_SIZE)) {
            getNotifications(page, showAction);
        }
    };

    const toggleDay = (date: string) => toggleOpenNotificationDay(setOpenDays, date);

    const handleRestore = async (productId: number) => {
        const result = await dispatch(
            restoreLiveGuidelineProductAction({ liveGuidelineId, productId }),
        );
        if (!restoreLiveGuidelineProductAction.fulfilled.match(result)) {
            errorModal.onOpen();
            return;
        }
        await getNotifications(currentPage, showAction);
    };

    return {
        pending,
        currentPage,
        totalPages,
        showAction,
        days,
        openDays,
        productsMap,
        errorModal,
        getNotifications,
        changePageNumber,
        handleShowActionChange,
        handleCurrentPage,
        toggleDay,
        handleRestore,
    };
};

const NotificationsTab: React.FC<Props> = ({ liveGuidelineId, liveGuidelineName }) => {
    const notifications = useNotificationData(liveGuidelineId);

    return (
        <>
            <NotificationsTabContainer>
                <NotificationControls
                    page={notifications.currentPage}
                    total={notifications.totalPages}
                    showAction={notifications.showAction}
                    onPageChange={notifications.changePageNumber}
                    onPageInput={notifications.handleCurrentPage}
                    onShowActionChange={notifications.handleShowActionChange}
                />
                <Spin spinning={notifications.pending}>
                    {!notifications.days.length && (
                        <EmptyData text="No notifications yet." pending={notifications.pending} />
                    )}
                    <NotificationDays
                        days={notifications.days}
                        openDays={notifications.openDays}
                        productsMap={notifications.productsMap}
                        liveGuidelineName={liveGuidelineName}
                        onToggleDay={notifications.toggleDay}
                        onRestore={notifications.handleRestore}
                    />
                </Spin>
                <NotificationsFilterRow style={{ marginTop: 16 }}>
                    <span />
                    <CustomPagination
                        page={notifications.currentPage}
                        total={notifications.totalPages}
                        changePageNumber={notifications.changePageNumber}
                        handleCurrentPage={notifications.handleCurrentPage}
                        pageSize={PAGE_SIZE}
                    />
                </NotificationsFilterRow>
            </NotificationsTabContainer>
            {notifications.errorModal.isOpen && (
                <ErrorModal
                    isOpen={notifications.errorModal.isOpen}
                    closeModal={notifications.errorModal.onClose}
                />
            )}
        </>
    );
};

export default NotificationsTab;
